import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import type { GanadoEnCorral } from '../../api-client/ganado/ganado';
import AppDialog from '../../components/ui/AppDialog';
import AppTextField from '../../components/ui/AppTextField';
import ComboBox from '../../components/ui/Combobox';
import { useGanadoStore } from '../../stores/ganado/ganado.store';
import { useUIStore } from '../../stores/ui/ui.store';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import EncabezadoDialogo from './EncabezadoDialogo';

interface BajaDialogoProps {
  open: boolean;
  onClose: () => void;
  animal: GanadoEnCorral;
}

const BajaDialogo = ({ open, onClose, animal }: BajaDialogoProps) => {
  const { loading } = useUIStore();
  const { darBaja } = useGanadoStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const form = useFormik({
    initialValues: {
      fechaBaja: dayjs(new Date()),
      motivo: -1,
      notas: ''
    },
    validationSchema: yup.object({
      fechaBaja: yup.date().required('La fecha de baja es requerida'),
      motivo: yup
        .number()
        .required('El motivo de baja es requerido')
        .min(0, 'El motivo de baja es requerido'),
      notas: yup.string().optional().max(100, 'Las notas no pueden exceder los 100 caracteres')
    }),
    onSubmit: async (values) => {
      await darBaja({
        folioInterno: animal.folioInterno,
        partida: animal.partida,
        idCorral: animal.idCorral,
        fechaBaja: values.fechaBaja.toDate(),
        motivo: values.motivo,
        notas: values.notas
      });

      onClose();
    }
  });

  const confirm = () => {
    form.submitForm();
    if (!form.isValid) return;
    setShowConfirm(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>BAJA</DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={form.submitForm}>
            <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
              <EncabezadoDialogo animal={animal} />
              <Grid size={6}>
                <Typography>Fecha baja:</Typography>
              </Grid>
              <Grid size={6}>
                <DatePicker
                  label=""
                  value={form.values.fechaBaja}
                  onChange={(date) => form.setFieldValue('fechaBaja', date)}
                  disabled={loading}
                />
              </Grid>
              <Grid size={6}>
                <Typography>Motivo:</Typography>
              </Grid>
              <Grid size={6}>
                <ComboBox
                  name="motivo"
                  label=""
                  options={[
                    { label: 'SELECCIONA UN MOTIVO', value: -1 },
                    { label: 'DECESO', value: 0 },
                    { label: 'VENTA', value: 1 },
                    { label: 'REMATE', value: 2 }
                  ]}
                  value={form.values.motivo}
                  disabled={loading}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.motivo && Boolean(form.errors.motivo)}
                  helperText={form.touched.motivo ? form.errors.motivo : undefined}
                />
              </Grid>
              <Grid size={12}>
                <AppTextField
                  name="notas"
                  label="Notas"
                  value={form.values.notas}
                  rows={3}
                  disabled={loading}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.notas && Boolean(form.errors.notas)}
                  maxLength={100}
                  helperText={getLengthOrErrorText(
                    form.touched.notas ? form.errors.notas : '',
                    form.values.notas.length,
                    100
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={() => setShowConfirm(true)}
            disabled={loading}
            startIcon={<CheckCircleIcon />}
            disableElevation
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            startIcon={<ExitToAppIcon />}
            onClick={onClose}
            disabled={loading}
            disableElevation
          >
            Salir
          </Button>
        </DialogActions>
      </Dialog>
      <AppDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Confirme"
        message="¿Está seguro de guardar y registrar el movimiento?"
        buttons={
          <>
            <Button color="secondary" variant="contained" disableElevation onClick={confirm}>
              Sí
            </Button>
            <Button variant="contained" disableElevation onClick={() => setShowConfirm(false)}>
              No
            </Button>
          </>
        }
      />
    </>
  );
};

export default BajaDialogo;
